import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyPanDeltaRaw,
  clampFocusPair,
  getCoverImagePanAxis,
  getCoverOverflow,
  needsRubberBandSnap,
  toRubberBandVisualFocus,
  type CoverImagePanAxis,
} from '../util/coverImageFocus';

const SNAP_DURATION_MS = 220;

function applyFocusToElement(
  element: HTMLElement,
  focusX: number,
  focusY: number,
) {
  element.style.backgroundPosition = `${focusX * 100}% ${focusY * 100}%`;
}

function clearInlineFocus(element: HTMLElement) {
  element.style.backgroundPosition = '';
}

function cancelFocusSnap(animationToken: { current: number }) {
  animationToken.current += 1;
}

function animateFocusSnap(
  element: HTMLElement,
  from: { focusX: number; focusY: number },
  to: { focusX: number; focusY: number },
  animationToken: { current: number },
  onComplete: () => void,
) {
  const token = animationToken.current + 1;
  animationToken.current = token;
  const startTime = performance.now();

  const tick = (now: number) => {
    if (animationToken.current !== token) {
      return;
    }

    const progress = Math.min(1, (now - startTime) / SNAP_DURATION_MS);
    const eased = 1 - (1 - progress) ** 3;
    const focusX = from.focusX + (to.focusX - from.focusX) * eased;
    const focusY = from.focusY + (to.focusY - from.focusY) * eased;

    applyFocusToElement(element, focusX, focusY);

    if (progress < 1) {
      requestAnimationFrame(tick);
      return;
    }

    onComplete();
  };

  requestAnimationFrame(tick);
}

interface UseCoverImagePanOptions {
  imageWidth: number;
  imageHeight: number;
  focusX: number;
  focusY: number;
  isEnabled: boolean;
  onFocusChange: (focusX: number, focusY: number) => void;
}

interface PanContext {
  axis: CoverImagePanAxis;
  overflowX: number;
  overflowY: number;
}

function buildPanContext(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): PanContext | null {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    imageWidth <= 0 ||
    imageHeight <= 0
  ) {
    return null;
  }

  const axis = getCoverImagePanAxis(
    containerWidth,
    containerHeight,
    imageWidth,
    imageHeight,
  );
  const { overflowX, overflowY } = getCoverOverflow(
    containerWidth,
    containerHeight,
    imageWidth,
    imageHeight,
  );

  return { axis, overflowX, overflowY };
}

export function useCoverImagePan({
  imageWidth,
  imageHeight,
  focusX,
  focusY,
  isEnabled,
  onFocusChange,
}: UseCoverImagePanOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ pointerX: 0, pointerY: 0, focusX, focusY });
  const dragFocusRef = useRef({ focusX, focusY });
  const dragVisualRef = useRef({ focusX, focusY });
  const dragContextRef = useRef<PanContext | null>(null);
  const isDraggingRef = useRef(false);
  const snapAnimationTokenRef = useRef(0);

  useEffect(() => {
    dragFocusRef.current = { focusX, focusY };
    dragVisualRef.current = { focusX, focusY };
  }, [focusX, focusY]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isEnabled || event.button !== 0) return;
      if ((event.target as HTMLElement).closest('button')) return;

      const context = buildPanContext(
        event.currentTarget.clientWidth,
        event.currentTarget.clientHeight,
        imageWidth,
        imageHeight,
      );
      if (!context || context.axis === 'none') return;

      cancelFocusSnap(snapAnimationTokenRef);
      clearInlineFocus(event.currentTarget);

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      isDraggingRef.current = true;
      dragContextRef.current = context;
      setIsDragging(true);
      dragStartRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        focusX,
        focusY,
      };
      dragFocusRef.current = { focusX, focusY };
      dragVisualRef.current = { focusX, focusY };
    },
    [focusX, focusY, imageWidth, imageHeight, isEnabled],
  );

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;

    const context = dragContextRef.current;
    if (!context) return;

    const deltaX = event.clientX - dragStartRef.current.pointerX;
    const deltaY = event.clientY - dragStartRef.current.pointerY;
    const rawFocus = applyPanDeltaRaw(
      dragStartRef.current.focusX,
      dragStartRef.current.focusY,
      deltaX,
      deltaY,
      context.axis,
      context.overflowX,
      context.overflowY,
    );
    const visualFocus = toRubberBandVisualFocus(rawFocus, context.axis);
    const clampedFocus = clampFocusPair(rawFocus);

    dragFocusRef.current = clampedFocus;
    dragVisualRef.current = visualFocus;
    applyFocusToElement(event.currentTarget, visualFocus.focusX, visualFocus.focusY);
  }, []);

  const finishDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      const context = dragContextRef.current;
      const clampedFocus = dragFocusRef.current;
      const visualFocus = dragVisualRef.current;
      const element = event.currentTarget;

      isDraggingRef.current = false;
      dragContextRef.current = null;
      setIsDragging(false);

      const commitFocus = () => {
        clearInlineFocus(element);
        if (
          clampedFocus.focusX !== focusX ||
          clampedFocus.focusY !== focusY
        ) {
          onFocusChange(clampedFocus.focusX, clampedFocus.focusY);
        }
      };

      if (context && needsRubberBandSnap(visualFocus, clampedFocus, context.axis)) {
        animateFocusSnap(
          element,
          visualFocus,
          clampedFocus,
          snapAnimationTokenRef,
          commitFocus,
        );
        return;
      }

      commitFocus();
    },
    [focusX, focusY, onFocusChange],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      finishDrag(event);
    },
    [finishDrag],
  );

  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      finishDrag(event);
    },
    [finishDrag],
  );

  const panHandlers = useMemo(
    () =>
      isEnabled
        ? {
            onPointerDown: handlePointerDown,
            onPointerMove: handlePointerMove,
            onPointerUp: handlePointerUp,
            onPointerCancel: handlePointerCancel,
          }
        : {},
    [
      handlePointerCancel,
      handlePointerDown,
      handlePointerMove,
      handlePointerUp,
      isEnabled,
    ],
  );

  const canPan = isEnabled && imageWidth > 0 && imageHeight > 0;

  return {
    canPan,
    isDragging,
    panHandlers: canPan ? panHandlers : {},
  };
}
